import moment from 'moment';
import {
  ActionType,
  AutoTextField,
  FormField,
  RadioGroupField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';

import * as builderFields from '../CreateBuilder/builderFields';
import {ComboFrequencyEnum} from '../../../schema-types/globalTypes';

export type contributePlanAndSubplanAmounts = {
  planProvider?: string | null;
  singleClientAmount: moneyFieldValueType;
  recurringClientAmount: moneyFieldValueType;
  singleEmployerAmount: moneyFieldValueType;
  recurringEmployerAmount: moneyFieldValueType;
  netSingleClientAmount?: moneyFieldValueType;
  netRecurringClientAmount?: moneyFieldValueType;
};

export type contributeFromType = {
  initialFee: builderFields.initialFeesFieldType;
  initialEmployerFee?: builderFields.initialFeesFieldType;
  legacyInitialFee: builderFields.DAFeeFieldType | null;
  legacyInitialEmployerFee: builderFields.DAFeeFieldType | null;
  maxAmount: number;
  id: string;
  parentPotId?: string;
  inSpecie?: boolean;
  full?: boolean;
  singleClientAmount: moneyFieldValueType;
  recurringClientAmount?: moneyFieldValueType;
  singleEmployerAmount?: moneyFieldValueType;
  recurringEmployerAmount?: moneyFieldValueType;
  netSingleClientAmount?: moneyFieldValueType;
  netRecurringClientAmount?: moneyFieldValueType;
  end?: builderFields.MomentDate;
  start?: builderFields.MomentDate;
  cashPotId?: string;
};

export type contributeDestinationValuesType = {
  existingPotId: string | null;
  parentPotId?: string;
};

export type contributeFieldType = {
  // USED BY: contributeHooks, contributeValidators, BuilderContributeSources, getContributionVariables
  comboFrequency: ComboFrequencyEnum;

  contributeTo: string | null;
  destinationValues: contributeDestinationValuesType;
  contributeFrom?: contributeFromType;
};

export const contributeAmountField: Omit<AutoTextField, 'id' | 'label'> = {
  type: ActionType.text,
  currency: true,
  decimals: 2,
  placeholder: '0',
  initialValue: '',
  allowZero: true,
  canBeEmpty: true,
};

export const contributeFrequencyComboField: SelectField = {
  id: 'contribute.comboFrequency',
  label: 'Frequency',
  type: ActionType.select,
  needsValue: true,

  options: [
    {
      value: ComboFrequencyEnum.SINGLE,
      label: 'Single',
    },
    {
      value: ComboFrequencyEnum.MONTHLY,
      label: 'Monthly',
    },
    {
      value: ComboFrequencyEnum.QUARTERLY,
      label: 'Quarterly',
    },
    {
      value: ComboFrequencyEnum.ANNUAL,
      label: 'Annual',
    },
    {
      value: ComboFrequencyEnum.MONTHLY_AND_SINGLE,
      label: 'Monthly + Single',
    },
    {
      value: ComboFrequencyEnum.QUARTERLY_AND_SINGLE,
      label: 'Quarterly + Single',
    },
    {
      value: ComboFrequencyEnum.ANNUAL_AND_SINGLE,
      label: 'Annual + Single',
    },
  ],
  initialValue: ComboFrequencyEnum.SINGLE,
};

export const contributeFullField: RadioGroupField = {
  id: 'full',
  label: 'Full redemption?',
  type: ActionType.radioGroup,
  initialValue: false,
  horizontal: true,
  fullWidth: true,
  horizontalOptionWidthXs: 6,
  options: [
    {label: 'Full', value: true},
    {label: 'Partial', value: false},
  ],
};

export const contributeInSpecieSourceField: RadioGroupField = {
  id: 'USE_SOURCE_ID_HERE_BY_SPREADING_THIS_AND_ADDING_SOURCE_ID',
  label: 'In specie?',
  type: ActionType.radioGroup,
  horizontal: true,
  horizontalOptionWidthXs: 6,
  initialValue: false,
  fullWidth: true,
  options: [
    {label: 'In specie', value: true},
    {label: 'Cash', value: false},
  ],
};

export const ContributePlanProviderField: FormField = {
  id: `contribute.planProvider`,
  label: 'Select plan provider',
  type: ActionType.selectAutocomplete,
  options: [],
  initialValue: '',
};
export const ContributeToField: FormField = {
  id: `contribute.contributeTo`,
  label: 'Contribute to',
  initialValue: '',
  type: ActionType.text,
};
export const ContributeAmountSingleField: AutoTextField = {
  ...contributeAmountField,
  label: 'Contribute (Single)',
  id: `contribute.contributeFrom.singleClientAmount`,
  allowZero: true,
  initialValue: '',
  currency: true,
  placeholder: '0',
};
export const ContributeAmountRecurrentField: AutoTextField = {
  ...contributeAmountField,
  label: 'Contribute (Recurrent)',
  id: `contribute.contributeFrom.recurringClientAmount`,
  initialValue: '',
};
export const ContributeEndField: FormField = {
  id: `contribute.contributeFrom.end`,
  label: 'End date',
  type: ActionType.datepicker,
  fullWidth: true,
  initialValue: undefined,
};
export const ContributePersonalSingleGrossField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.singleClientAmount',
  label: 'Personal Single Amount (Gross)',
};
export const ContributePersonalSingleNetField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.netSingleClientAmount',
  label: 'Personal Single Amount (Net)',
  disabled: true,
};
export const ContributePersonalRecurringGrossField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.recurringClientAmount',
  label: 'Personal Recurring Amount (Gross)',
};
export const ContributePersonalRecurringNetField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.netRecurringClientAmount',
  label: 'Personal Recurring Amount (Net)',
  disabled: true,
};
export const ContributeEmployerSingleField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.singleEmployerAmount',
  label: 'Employer Single Amount (Gross)',
};
export const ContributeEmployerRecurringField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.recurringEmployerAmount',
  label: 'Employer Recurring Amount (Gross)',
};

export const ContributeStartField: FormField = {
  id: `contribute.contributeFrom.start`,
  label: 'Start date',
  type: ActionType.datepicker,
  fullWidth: true,
  shouldDisableDate: (day) => {
    const dateMoment = moment.utc(day);
    const date = dateMoment?.get('date');
    if (!date) {
      return true;
    }
    if (date === 1) {
      return false;
    }
    if (date === 15) {
      return false;
    }
    return true;
  },
};
